<template>
  <div class="scanner-container">
    <!-- Header -->
    <header class="app-header">
      <!-- Left section: Logo and User Info -->
      <div class="header-left">
        <div class="logo-container">
          <img
            src="@/assets/StaffTrainingLogo.png"
            alt="Company Logo"
            class="company-logo"
          >
        </div>
        <div class="user-info">
          IMS User Portal
        </div>
      </div>

      <!-- Right section: Admin and Logout/Profile Buttons -->
      <div class="header-right">
        <div
          v-if="isAdmin"
          class="admin-navigation"
        >
          <button
            class="admin-button"
            @click="navigateToAdminDashboard"
          >
            <span class="admin-text">Admin</span>
            <span class="admin-icon">➡️</span>
          </button>
        </div>
        <div class="logout-profile-container">
          <button
            class="logout-button"
            @click="handleLogout"
          >
            Logout
          </button>
          <button
            class="profile-button"
            title="Profile"
            @click="navigateToProfile"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="24"
              height="24"
              fill="white"
            >
              <path
                d="
                M12 2a5 5 0 1 1 0 10
                5 5 0 0 1 0-10zm0 12
                c-4.42 0-8 3.58-8 8h16
                c0-4.42-3.58-8-8-8z
              "
              />
            </svg>
          </button>
        </div>
      </div>
    </header>

    <!-- Background Image for Start Scanning Button -->
    <div
      v-if="!scanning && !itemDetails && !itemScanned"
      class="start-button-background"
    >
      <img
        src="@/assets/ScannerBackground.png"
        alt="Background"
        class="background-image"
      >
    </div>

    <!-- Start Scanning Button -->
    <div
      v-if="!scanning && !itemDetails && !itemScanned"
      class="start-button"
    >
      <button @click="startScanning">
        Click to Start Scanning
      </button>
    </div>

    <!-- QR Code Scanner -->
    <div
      v-if="scanning"
      class="video-frame-container"
    >
      <!-- QR Code Scanner Stream -->
      <div class="video-frame">
        <qrcode-stream
          @decode="onDecode"
          @init="onInit"
        >
          <div
            v-if="errorMessage"
            class="overlay"
          >
            <p>{{ errorMessage }}</p>
          </div>
        </qrcode-stream>

        <!-- Corner Borders -->
        <div class="corner-borders">
          <div class="top-left" />
          <div class="top-right" />
          <div class="bottom-left" />
          <div class="bottom-right" />
        </div>
      </div>
    </div>

    <!-- Scan Message -->
    <p
      v-if="scanning"
      class="scan-message"
    >
      Point your camera at the QR Code to scan your item
    </p>

    <!-- Exit Button -->
    <button
      v-if="scanning"
      class="exit-button"
      @click="exitScanning"
    >
      Exit
    </button>

    <!-- Product Information Display -->
    <div
      v-if="itemDetails"
      class="item-details-container"
    >
      <div class="item-details-frame">
        <!-- Item Name -->
        <h2 class="item-name">
          {{ itemDetails.name }}
        </h2>

        <!-- Item Image -->
        <img
          v-if="itemDetails.imageUrl"
          :src="itemDetails.imageUrl"
          alt="Item Image"
          class="item-image"
        >

        <!-- Item Information -->
        <div class="item-info">
          <p><strong>Type:</strong> {{ itemDetails.type }}</p>
          <p><strong>Description:</strong> {{ itemDetails.description }}</p>
          <p><strong>Current Stock:</strong> {{ itemDetails.stock }}</p>
        </div>
      </div>
    </div>

    <!-- Confirmation Section -->
    <div
      v-if="itemDetails && !confirmed && !scanAnother"
      class="confirmation-section initial-confirmation-section"
    >
      <!-- Confirmation Text -->
      <p class="confirmation-text">
        Is this your item? Click confirm to continue.
      </p>

      <!-- Initial Confirmation Buttons -->
      <div class="initial-confirmation-buttons">
        <button
          class="initial-confirm-button"
          @click="confirmItem"
        >
          Confirm
        </button>
        <button
          class="initial-back-button"
          @click="resetScannerForNextItem"
        >
          Back
        </button>
      </div>
    </div>

    <!-- Quantity Input Section -->
    <div
      v-if="confirmed && !scanAnother"
      class="confirmation-section quantity-confirmation-section"
    >
      <!-- Quantity Input and Instruction -->
      <p class="confirmation-text">
        Enter quantity and select Check In or Check Out:
      </p>

      <!-- Aligned container for quantity input and buttons -->
      <div class="quantity-action-container">
        <input
          v-model.number="quantity"
          type="number"
          min="1"
          class="quantity-input"
        >
        <div class="quantity-confirmation-buttons">
          <button
            class="quantity-checkout-button"
            @click="checkOutItem"
          >
            Check Out
          </button>
          <button
            class="quantity-checkin-button"
            @click="checkInItem"
          >
            Check In
          </button>
        </div>
      </div>
    </div>

    <!-- Scan Another Item or Finish -->
    <div
      v-if="scanAnother"
      class="confirmation-section scan-another-section"
    >
      <!-- Action Successful Text -->
      <p class="confirmation-text">
        Completed. Would you like to scan another item?
      </p>

      <!-- Scan Another Item or Review Checked Items Buttons -->
      <div class="scan-another-buttons">
        <button
          class="scan-another-button"
          @click="resetScannerForNextItem"
        >
          Yes
        </button>
        <button
          class="review-items-button"
          @click="goToReviewItemsPage"
        >
          Review Checked Items
        </button>
      </div>
    </div>

    <div
      v-if="showOrientationPrompt"
      class="orientation-modal"
    >
      <div class="orientation-modal-content">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 512 512"
          width="64"
          height="64"
          fill="#00A3DB"
        >
          <path
            d="
                M338.13,82.2C398.08,110.62,441,168.73,447.58,237.67h27.5
                C465.73,124.73,371.32,36,256,36l-12.1.55,69.85,69.85ZM223.55,68.08
                a27.35,27.35,0,0,0-38.87,0L68.08,184.68a27.35,27.35,0,0,0,0,38.87
                L288.45,443.92a27.35,27.35,0,0,0,38.87,0l116.6-116.6
                a27.35,27.35,0,0,0,0-38.87Zm84.33,356.4L87.52,204.12
                l116.6-116.6L424.48,307.88Zm-134,5.32A192.25,192.25,0,0,1,64.42,274.33
                H36.92C46.27,387.27,140.68,476,256,476l12.1-.55L198.25,405.6Z
              "
          />
        </svg>
        <p>{{ orientationMessage }}</p>
      </div>
    </div>

    <!-- Footer -->
    <footer class="app-footer">
      <div class="footer-content">
        <img
          src="@/assets/ParentLogo.png"
          alt="Parent Company Logo"
          class="footer-logo"
        >
        <p>© 2024 Staff Nursing Ltd. All rights reserved.</p>
      </div>
    </footer>
  </div>
</template>

<script>
import { QrcodeStream } from 'vue3-qrcode-reader';
import axios from 'axios';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'QrScanner',
  components: {
    QrcodeStream,
  },
  data() {
    return {
      scanning: false,
      decodedData: '',
      itemDetails: null,
      confirmed: false,
      errorMessage: '',
      quantity: 1,
      scanAnother: false,
      itemScanned: false,
      isAdmin: false,
      startScanningImmediately: false,
      isComponentAlive: true,
      showOrientationPrompt: false,
      orientationMessage: '',
    };
  },
  computed: {
    ...mapGetters(['getCheckedItems']),
  },
  watch: {
    $route() {
      this.checkOrientation();
    },
    showOrientationPrompt() {
    },
  },
  async created() {
    await this.checkUserRole();
    this.checkOrientation();
    window.addEventListener('orientationchange', this.checkOrientation);
  },
  mounted() {
    this.isComponentAlive = false; // Set to false when unmounting
    if (this.startScanningImmediately) {
      this.startScanning();
    }
  },
  beforeUnmount() {
    this.isComponentAlive = false;
    window.removeEventListener('orientationchange', this.checkOrientation);
  },
  methods: {
    checkOrientation() {
      setTimeout(() => {
        const mediaQueryPortrait = window.matchMedia('(orientation: portrait)');
        const mediaQueryLandscape = window.matchMedia('(orientation: landscape)');
        const isPortrait = mediaQueryPortrait.matches;
        const isLandscape = mediaQueryLandscape.matches;

        const isDesktop = window.innerWidth >= 1024;
        if (isDesktop) {
          this.showOrientationPrompt = false;
          return;
        }

        if (this.$route.name === 'AdminDashboard' && isPortrait) {
          this.showOrientationPrompt = true;
          this.orientationMessage = 'Please switch to landscape mode for better viewing.';
        } else if (this.$route.name === 'QrScanner' && isLandscape) {
          this.showOrientationPrompt = true;
          this.orientationMessage = 'Please switch to portrait mode for scanning.';
        } else {
          this.showOrientationPrompt = false; // Clear prompt if orientation is correct
        }
      }, 100);
    },
    ...mapActions(['addItem', 'removeItem', 'resetCheckedItems']),
    async checkUserRole() {
      try {
        const token = localStorage.getItem('authToken');
        const response = await axios.post('https://api.inventory.stafftraining.me/api/verifyToken', {}, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        this.isAdmin = response.data.role === 'admin';
      } catch (error) {
        console.error('Error verifying user role:', error);
      }
    },
    startScanning() {
      this.$nextTick(() => {
        this.scanning = true;
        this.scanAnother = false;
        this.itemDetails = null;
        this.confirmed = false;
        this.itemScanned = false;
      });
    },
    exitScanning() {
      if (this.scanning) {
        this.scanning = false;
        this.errorMessage = '';
      }
    },
    resetScannerForNextItem() {
      this.itemDetails = null;
      this.confirmed = false;
      this.scanAnother = false;
      this.scanning = true;
      this.quantity = 1;
      this.itemScanned = false;
    },
    async onDecode(decodedString) {
      this.decodedData = decodedString;
      this.itemScanned = true;

      try {
        const response = await axios.get('https://api.inventory.stafftraining.me/api/getItemDetails', {
          params: {
            itemID: decodedString,
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        });

        if (response.data && response.data.success) {
          this.itemDetails = response.data.item;
          this.exitScanning();
        } else {
          // eslint-disable-next-line no-alert
          alert('Item not found.');
        }
      } catch (error) {
        console.error('Error fetching item details:', error);
        // eslint-disable-next-line no-alert
        alert('Error fetching item details.');
      }
    },
    confirmItem() {
      if (!this.itemDetails) {
        // eslint-disable-next-line no-alert
        alert('No item selected.');
        return;
      }
      this.confirmed = true;
    },

    async checkOutItem() {
      if (this.quantity < 1) {
        // eslint-disable-next-line no-alert
        alert('Quantity must be at least 1');
        return;
      }
      if (this.itemDetails.stock < this.quantity) {
        // eslint-disable-next-line no-alert
        alert('Insufficient stock available to check out this quantity.');
        return;
      }

      let totalItemsToCheckOut = this.quantity;
      let updatedBoxesInStock = null;

      // Check if it's a consumable item
      if (this.itemDetails.type === 'consumable') {
        const fullBoxesToCheckOut = Math.floor(this.quantity / this.itemDetails.itemsPerBox);
        const remainingItems = this.quantity % this.itemDetails.itemsPerBox;

        totalItemsToCheckOut = fullBoxesToCheckOut * this.itemDetails.itemsPerBox + remainingItems;

        // Update stock and box count directly in this method
        this.itemDetails.stock -= totalItemsToCheckOut; // Subtract the checked-out items from stock
        updatedBoxesInStock = Math.floor(this.itemDetails.stock / this.itemDetails.itemsPerBox);
        this.itemDetails.boxesInStock = updatedBoxesInStock; // Update box count for consumables
      } else {
        // For reusable items, just subtract the quantity from stock
        this.itemDetails.stock -= this.quantity;
      }

      try {
        // Create the payload for updating the backend
        const payload = {
          itemID: this.decodedData,
          action: 'checkOut',
          quantity: totalItemsToCheckOut,
        };

        if (this.itemDetails.type === 'reusable') {
          payload.isComplete = false; // Mark as not complete when checking out a reusable item
        }

        // Add boxesInStock only if it's a consumable
        if (this.itemDetails.type === 'consumable') {
          payload.boxesInStock = updatedBoxesInStock;
        }

        const response = await axios.post('https://api.inventory.stafftraining.me/api/updateStock', payload, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        });

        if (response.data && response.data.success) {
          this.addItem({
            itemID: this.itemDetails.itemID,
            name: this.itemDetails.name,
            quantity: totalItemsToCheckOut,
            action: 'checkOut',
            imageUrl: this.itemDetails.imageUrl,
          });

          // eslint-disable-next-line no-alert
          alert('Item checked out successfully.');
          await this.fetchItemDetails();
          this.scanAnother = true;
          this.confirmed = false;
        } else {
          // eslint-disable-next-line no-alert
          alert('Error checking out item.');
        }
      } catch (error) {
        console.error('Error checking out item:', error);
        // eslint-disable-next-line no-alert
        alert('Error checking out item.');
      }
    },

    async checkInItem() {
      if (this.quantity < 1) {
        // eslint-disable-next-line no-alert
        alert('Quantity must be at least 1');
        return;
      }

      let totalItemsToCheckIn = this.quantity;
      let updatedBoxesInStock = null;

      // Check if it's a consumable item
      if (this.itemDetails.type === 'consumable') {
        const fullBoxesToCheckIn = Math.floor(this.quantity / this.itemDetails.itemsPerBox);
        const remainingItems = this.quantity % this.itemDetails.itemsPerBox;

        totalItemsToCheckIn = fullBoxesToCheckIn * this.itemDetails.itemsPerBox + remainingItems;

        // Update stock and box count directly in this method
        this.itemDetails.stock += totalItemsToCheckIn; // Add the checked-in items to stock
        updatedBoxesInStock = Math.floor(this.itemDetails.stock / this.itemDetails.itemsPerBox);
        this.itemDetails.boxesInStock = updatedBoxesInStock; // Update box count for consumables
      }

      try {
        // Create the payload for updating the backend
        const payload = {
          itemID: this.decodedData,
          action: 'checkIn',
          quantity: totalItemsToCheckIn,
        };

        if (this.itemDetails.type === 'reusable') {
          // Fetch unresolved logs for reusable items
          const unresolvedLogIDs = await this.fetchUnresolvedLogs(this.itemDetails.itemID);

          if (unresolvedLogIDs.length < this.quantity) {
            // eslint-disable-next-line no-alert
            alert('Not enough unresolved logs to perform the check-in.');
            return;
          }

          // Include the unresolved log IDs in the payload
          payload.unresolvedLogIDs = unresolvedLogIDs.slice(0, this.quantity);
        }

        // Add boxesInStock only if it's a consumable
        if (this.itemDetails.type === 'consumable') {
          payload.boxesInStock = updatedBoxesInStock;
        }

        const response = await axios.post('https://api.inventory.stafftraining.me/api/updateStock', payload, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        });

        if (response.data && response.data.success) {
          this.addItem({
            itemID: this.itemDetails.itemID,
            name: this.itemDetails.name,
            quantity: totalItemsToCheckIn,
            action: 'checkIn',
            imageUrl: this.itemDetails.imageUrl,
          });

          // eslint-disable-next-line no-alert
          alert('Item checked in successfully.');
          await this.fetchItemDetails();
          this.scanAnother = true;
          this.confirmed = false;
        } else {
          // eslint-disable-next-line no-alert
          alert(response.data?.message || 'Error checking in item.');
        }
      } catch (error) {
        const errorMessage = error.response?.data?.message || 'Error checking in item.';
        console.error('Error checking in item:', errorMessage);
        // eslint-disable-next-line no-alert
        alert(errorMessage);
      }
    },

    async fetchUnresolvedLogs(itemID) {
      try {
        const response = await axios.get('https://api.inventory.stafftraining.me/api/getUnresolvedLogs', {
          params: { itemID },
          headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
        });

        if (response.data?.success && Array.isArray(response.data.logs)) {
          // eslint-disable-next-line no-console
          console.log('Unresolved logs fetched:', response.data.logs);
          return response.data.logs.map((log) => log.log_ID); // Extract and return log IDs
        }

        const message = response.data?.message || 'Failed to fetch unresolved logs.';
        this.$toast.error(message);
        console.warn('Unresolved logs fetch failed:', message);
        return [];
      } catch (error) {
        // Add logging to debug the structure of the error object
        console.error('Error object received:', error);

        const errorMessage = error?.response?.data?.message || error.message || 'An unknown error occurred.';
        console.error('Error fetching unresolved logs:', errorMessage);
        this.$toast.error(`Error fetching unresolved logs: ${errorMessage}`);
        return [];
      }
    },

    async fetchItemDetails() {
      try {
        const response = await axios.get('https://api.inventory.stafftraining.me/api/getItemDetails', {
          params: {
            itemID: this.decodedData,
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        });

        if (response.data && response.data.success) {
          this.itemDetails = response.data.item; // Refresh item details from backend
        } else {
          // eslint-disable-next-line no-alert
          alert('Error fetching item details after update.');
        }
      } catch (error) {
        console.error('Error fetching item details after update:', error);
        // eslint-disable-next-line no-alert
        alert('Error fetching item details after update.');
      }
    },

    goToReviewItemsPage() {
      this.$router.push({ name: 'ReviewCheckedItems' });
    },

    onInit(promise) {
      promise
        .catch((error) => {
          this.errorMessage = 'Camera permission denied or not available.';
          console.error(error);
        });
    },
    navigateToAdminDashboard() {
      this.$router.push({ name: 'AdminDashboard' });
    },
    navigateToProfile() {
      this.$router.push({ name: 'ProfilePage' });
    },
    handleSuccessfulScan(item) {
      this.addItem(item);
    },
    handleRemoveItem(itemId) {
      this.removeItem(itemId);
    },
    handleLogout() {
      localStorage.removeItem('authToken');
      this.$router.push('/');
    },
  },
};
</script>

<style>
:root {
  --primary-color: #191732;
  --primary-hover-color: #4B47E7;
  --primary-active-color: #00A3DB;
  --secondary-color: #262626;
  --highlight-color: #00A3DB;
  --text-title-color: #191732;
  --text-paragraph-color: #000;
  --text-light-color: #fff;
  --font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue";
  --button-radius: 12px;
  --button-padding: 6px;
  --element-margin: 12px;
}

body, html {
  margin: 0;
  padding: 0;
  height: 100%;
  width: 100%;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue";
}

/* Styles for header */
.app-header {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100px; /* Adjust height as needed */
  background-color: var(--secondary-color);
  color: var(--text-light-color);
  display: flex;
  align-items: center;
  justify-content: space-between; /* Space between left and right sections */
  padding: 0 20px;
  box-sizing: border-box;
  z-index: 10;
}

/* Left section: Logo and User Info */
.header-left {
  display: flex;
  align-items: center;
}

.logo-container {
  display: flex;
  align-items: center;
}

.company-logo {
  height: 64px; /* Adjust logo height */
}

.user-info {
  color: var(--text-light-color);
  font-size: 0.9em;
  margin-left: 20px; /* Spacing between logo and user info */
}

/* Right section: Admin and Logout buttons */
.header-right {
  display: flex;
  align-items: center;
}

.admin-navigation {
  display: flex;
  align-items: center;
  margin-right: 10px; /* Reduce margin for better spacing on mobile */
}

.admin-button {
  background-color: var(--highlight-color);
  color: var(--text-light-color);
  border: none;
  border-radius: var(--button-radius);
  padding: 8px 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px; /* Space between icon and text */
  overflow: hidden;
  box-sizing: border-box;
  margin-right: 0px; /* Space between admin and logout buttons */
}

.admin-button:hover {
  background-color: var(--primary-hover-color);
}

.admin-button:active {
  background-color: var(--primary-active-color);
}

/* Flex container for logout and profile buttons */
.logout-profile-container {
  display: flex; /* Align items horizontally */
  gap: 10px; /* Add space between buttons */
  align-items: center; /* Vertically center content */
}

.logout-button,
.profile-button {
  background-color: #00A3DB;
  color: white;
  border: none;
  padding: 8px 12px;
  cursor: pointer;
  border-radius: 12px;
  display: flex;
  align-items: center; /* Ensure icon/text alignment */
  justify-content: center;
}

.logout-button:hover,
.profile-button:hover {
  background-color: #2980b9; /* Darker blue on hover */
}

.profile-button svg {
  display: block; /* Ensure SVG does not cause spacing issues */
}

/* Scanner container */
.scanner-container {
  position: relative;
  top: 100px;
  width: 100%;
  height: calc(100vh - 100px); /* Adjust height to fit header (100px) and footer (120px) */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start; /* No need for center if we are aligning it below the header */
  overflow: hidden; /* Enable vertical scrolling only for the scanner container */
  box-sizing: border-box;
}

/* Background image for Start Scanning button */
.start-button-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0.2;
  z-index: -1;
}

.start-button-background img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Start Scanning Button */
.start-button {
  position: fixed; /* Position relative to the viewport */
  top: 50%; /* Center vertically in viewport */
  left: 50%; /* Center horizontally in viewport */
  transform: translate(-50%, -50%); /* Adjust to perfectly center */
  z-index: 10; /* Ensure it's above other elements */
}

.start-button button {
  width: 200px;
  height: 200px;
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  background-color: var(--highlight-color);
  color: var(--text-light-color); /* Set the text color to white */
  font-size: 1.3em;
  font-weight: bold;
  border: none; /* Remove any border that might be appearing */
  cursor: pointer; /* Ensure cursor indicates a clickable button */
  transition: background-color 0.3s ease; /* Smooth transition for hover */
}

/* Adjusting the Video Frame Container */
.video-frame-container {
  position: relative;
  margin-top: 40px; /* Adjust the margin to position it slightly lower */
  display: flex;
  flex-direction: column;
  align-items: center; /* Center the video frame and scan message horizontally */
  z-index: 5; /* Make sure it appears above other elements */
}

.video-frame {
  width: 320px;
  height: 320px;
  overflow: hidden;
  border: none;
  padding: 10px;
  margin-bottom: 10px; /* Add some space below the video feed for the scan message */
}

/* Styles for the scan message */
.scan-message {
  margin-top: 10px;
  text-align: center;
  font-weight: bold;
  color: var(--text-title-color);
}

/* Corner borders */
.corner-borders {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 3;
}

.corner-borders .top-left {
  position: absolute;
  top: 0;
  left: 0;
  width: 60px;
  height: 60px;
  border-top: 3pt solid #4B47E7;
  border-left: 3pt solid #4B47E7;
}

.corner-borders .bottom-right {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 60px;
  height: 60px;
  border-bottom: 3pt solid #4B47E7;
  border-right: 3pt solid #4B47E7;
}

.corner-borders .top-right {
  position: absolute;
  top: 0;
  right: 0;
  width: 60px;
  height: 60px;
  border-top: 3pt solid #4B47E7;
  border-right: 3pt solid #4B47E7;
}

.corner-borders .bottom-left {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 60px;
  height: 60px;
  border-bottom: 3pt solid #4B47E7;
  border-left: 3pt solid #4B47E7;
}

/* Positioning the Exit Button in Bottom Right Corner */
.exit-button {
  position: fixed; /* Fix button to the viewport */
  bottom: 140px; /* Positioned above the footer (footer height is 120px + margin) */
  right: 24px; /* Distance from the right side of the viewport */
  background-color: var(--primary-color);
  color: var(--text-light-color);
  border: none;
  border-radius: var(--button-radius);
  padding: 12px; /* Increased padding for better width */
  width: 180px; /* Fixed width for better button visibility */
  cursor: pointer;
  font-size: 1em;
  transition: background-color 0.3s ease;
  z-index: 15; /* Make sure the button is above other elements */
}

.exit-button:hover {
  background-color: var(--primary-hover-color);
}

.exit-button:active {
  background-color: var(--primary-active-color);
}

/* Updated Confirmation Buttons */
.initial-confirm-button,
.initial-back-button,
.quantity-checkout-button,
.quantity-checkin-button,
.scan-another-button,
.review-items-button {
  width: 120px;
  height: 40px;
  font-size: 0.8em;
  padding: 8px;
  background-color: var(--primary-color);
  color: var(--text-light-color);
  border: none;
  border-radius: var(--button-radius);
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.initial-confirm-button:hover,
.initial-back-button:hover,
.quantity-checkout-button:hover,
.quantity-checkin-button:hover,
.scan-another-button:hover,
.review-items-button:hover {
  background-color: var(--primary-hover-color);
}

.initial-confirm-button:active,
.initial-back-button:active,
.quantity-checkout-button:active,
.quantity-checkin-button:active,
.scan-another-button:active,
.review-items-button:active {
  background-color: var(--primary-active-color);
}

/* Add gap between buttons in confirmation sections */
.initial-confirmation-buttons,
.quantity-confirmation-buttons,
.scan-another-buttons {
  display: flex;
  gap: 15px; /* Adjust this value as needed for the desired space between buttons */
  justify-content: center;
}

/* Quantity Input and Buttons Container */
.quantity-action-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  margin-top: 20px;
  padding: 10px;
  flex-wrap: wrap;
}

.quantity-input {
  width: 80px;
  height: 40px;
  text-align: center;
  font-size: 1em;
  appearance: textfield;
  -webkit-appearance: textfield;
}

.quantity-input::-webkit-inner-spin-button,
.quantity-input::-webkit-outer-spin-button {
  -webkit-appearance: auto;
}

/* Overlay styling for scanner errors */
.overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2;
}

/* Styles for the scan message */
.scan-message {
  width: 90%;
  text-align: center;
  margin: 20px auto;
  font-weight: bold;
  color: var(--text-title-color);
}

.item-details-container {
  margin-top: 30px; /* The header height is 100px + 20px gap */
  width: 100%;
  display: flex;
  justify-content: center; /* Center the container horizontally */
  box-sizing: border-box;
}

/* Container for the entire item details section */
.item-details-frame {
  width: 90%;
  max-width: 340px;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 12px;
  background-color: var(--background-color);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
  margin-bottom: 10px;
}

.item-details-frame .item-name {
  margin: 0;
  padding-bottom: 0px;
  font-size: 1em;
  font-weight: bold;
  text-align: left;
}

.item-details-frame .item-image {
  max-width: 75%;
  height: auto;
  margin: auto;
  display: block;
  border-radius: 8px;
}

.item-details-frame .item-info {
  margin-top: 0px;
  font-size: 0.9em;
  text-align: left;
}

/* Orientation Modal Styles */
.orientation-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  pointer-events: auto; /* Prevent it from intercepting clicks */
}

.orientation-modal-content {
  background: #fff;
  padding: 20px;
  border-radius: 10px;
  text-align: center;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  max-width: 300px;
  width: 90%;
}

.orientation-modal-content svg {
  margin-bottom: 15px;
}

.orientation-modal-content p {
  font-size: 16px;
  color: #333;
}

/* Styles for footer */
.app-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: 100px;
  background-color: var(--secondary-color);
  color: var(--text-light-color);
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  z-index: 1000;
  padding: 4px;
  text-align: center;
}

/* Styles for footer logo */
.footer-logo {
  max-width: 100%;
  height: auto;
  max-height: 60px;
  align-self: center;
}

/* Styles for footer text */
.app-footer p {
  font-size: 11px;
  text-align: center;
}

/* Responsive tweaks for smaller screens */
@media (max-width: 380px), (max-height: 800px) {
  .company-logo {
    height: 40px;
  }

  .app-header {
    height: 80px;
  }

  .user-info {
  font-size: 0.7em;

  }

  .video-frame-container {
    margin-top: 25px;
  }
  .video-frame {
    width: 240px;
    height: 240px;
    margin: 0 auto;
  }

  .item-details-container {
    margin-top: 10px;
  }

  .item-details-frame {
    width: 80%;
  }

  .item-details-frame .item-name {
    font-size: 0.75em;
  }

  .item-details-frame .item-info {
    font-size: 0.75em;
    line-height: 1.2;
  }

  .quantity-input {
  width: 60px;
  height: 40px;
  text-align: center;
  font-size: 0.9em;
  appearance: textfield;
  -webkit-appearance: number-input; /* Ensure spinners are visible on webkit-based browsers */
  appearance: number-input;
  }

  .quantity-input::-webkit-inner-spin-button,
  .quantity-input::-webkit-outer-spin-button {
    -webkit-appearance: auto;
  }

  .quantity-confirmation-buttons button {
    width: 80px;
    height: 40px;
    font-size: 0.75em;
  }

  .app-footer {
    height: 90px;
  }

  .footer-logo {
    height: 40px;
  }

  .app-footer p {
    font-size: 0.8em;
  }
}

</style>
